import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiImage,
  FiUpload,
  FiX,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { galleryAPI } from "../../services/api";
import { resolveImageUrl } from "../../utils/helpers";
import { triggerRefresh } from "../../hooks/useDataRefresh";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const CATEGORIES = ["Campus", "Events", "Sports", "Academic", "Cultural", "Other"];

const catColors = {
  Campus: "bg-blue-100 text-blue-700",
  Events: "bg-emerald-100 text-emerald-700",
  Sports: "bg-orange-100 text-orange-700",
  Academic: "bg-violet-100 text-violet-700",
  Cultural: "bg-pink-100 text-pink-700",
  Other: "bg-slate-100 text-slate-600",
};

const gradients = [
  "from-blue-400 to-blue-600",
  "from-emerald-400 to-emerald-600",
  "from-violet-400 to-violet-600",
  "from-sky-400 to-sky-600",
  "from-indigo-400 to-indigo-600",
  "from-pink-400 to-pink-600",
];

const emptyForm = { title: "", description: "", category: "Events", image: null };
const emptyBulkForm = { category: "Events", images: [] };

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [bulkForm, setBulkForm] = useState(emptyBulkForm);
  const [bulkPreviews, setBulkPreviews] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await galleryAPI.getAdmin();
        setImages(res.data?.data?.images || []);
      } catch {
        toast.error("Failed to load gallery images");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((p) => ({ ...p, image: file }));
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleBulkFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBulkForm((p) => ({ ...p, images: files }));
    const previews = [];
    let loaded = 0;
    files.forEach((file, idx) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        previews.push({ file, preview: ev.target.result });
        loaded++;
        if (loaded === files.length) setBulkPreviews(previews);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUpload = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.image) { toast.error("Please select an image"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const res = await galleryAPI.upload(fd);
      const newItem = res.data?.data?.image;
      if (newItem) setImages((prev) => [newItem, ...prev]);
      toast.success("Image uploaded successfully");
      setModal(false);
      setForm(emptyForm);
      setPreview(null);
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload image. Check your Cloudinary settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkForm.images.length) { toast.error("Please select at least one image"); return; }
    if (!bulkForm.title?.trim()) { toast.error("Please enter a title for the images"); return; }
    setBulkSaving(true);
    try {
      const fd = new FormData();
      bulkForm.images.forEach((img) => fd.append("images", img));
      fd.append("title", bulkForm.title);
      fd.append("category", bulkForm.category);
      const res = await galleryAPI.uploadBulk(fd);
      const failedItems = res.data?.data?.failed || [];
      const newItems = res.data?.data?.images || [];
      const allRes = await galleryAPI.getAdmin();
      setImages(allRes.data?.data?.images || []);
      if (failedItems.length > 0) {
        toast.warning(`${newItems.length} uploaded, ${failedItems.length} failed`);
      } else {
        toast.success(`${newItems.length} image(s) uploaded successfully`);
      }
      setBulkModal(false);
      setBulkForm(emptyBulkForm);
      setBulkPreviews([]);
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload images.");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await galleryAPI.togglePublish(id);
      setImages((prev) => prev.map((i) => i._id === id ? { ...i, isPublished: !i.isPublished } : i));
      toast.success("Status updated");
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await galleryAPI.delete(id);
      setImages((prev) => prev.filter((i) => i._id !== id));
      setDeleteConfirm(null);
      toast.success("Image deleted");
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete image");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedForDelete.size) { toast.error("Please select at least one image"); return; }
    setSaving(true);
    try {
      for (const id of Array.from(selectedForDelete)) {
        await galleryAPI.delete(id);
      }
      setImages((prev) => prev.filter((i) => !selectedForDelete.has(i._id)));
      setSelectedForDelete(new Set());
      toast.success(`${selectedForDelete.size} image(s) deleted`);
      triggerRefresh("gallery");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete images");
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectImage = (id) => {
    const n = new Set(selectedForDelete);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelectedForDelete(n);
  };

  const toggleSelectAll = () => {
    setSelectedForDelete(selectedForDelete.size === images.length ? new Set() : new Set(images.map((i) => i._id)));
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Gallery Management</h2>
          <p className="text-slate-500 text-sm mt-1">Upload and manage school photos</p>
        </div>
        <div className="flex gap-3">
          {selectedForDelete.size > 0 && (
            <button onClick={handleBulkDelete} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors text-sm disabled:opacity-50">
              <FiTrash2 className="w-4 h-4" /> Delete ({selectedForDelete.size})
            </button>
          )}
          <button onClick={() => { setBulkForm(emptyBulkForm); setBulkPreviews([]); setBulkModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 transition-colors text-sm shadow-md">
            <FiUpload className="w-4 h-4" /> Upload Multiple
          </button>
          <button onClick={() => { setForm(emptyForm); setPreview(null); setModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1D4ED8] text-white rounded-xl font-semibold hover:bg-[#1e3a8a] transition-colors text-sm shadow-md">
            <FiPlus className="w-4 h-4" /> Upload Image
          </button>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div>
          {images.length > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-white rounded-xl border border-blue-100 shadow-sm">
              <input type="checkbox"
                checked={images.length > 0 && selectedForDelete.size === images.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 cursor-pointer rounded accent-blue-600"
              />
              <span className="text-sm text-slate-500">
                {selectedForDelete.size > 0 ? `${selectedForDelete.size} selected` : "Select multiple images to delete them"}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, idx) => (
              <motion.div
                key={image._id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, delay: idx * 0.04 }}
                className={`bg-white border rounded-2xl overflow-hidden shadow-sm group relative transition-all ${
                  selectedForDelete.has(image._id) ? "border-red-300 ring-2 ring-red-200" : "border-blue-100 hover:shadow-md"
                }`}
              >
                <div className="absolute top-2 left-2 z-10">
                  <input type="checkbox" checked={selectedForDelete.has(image._id)}
                    onChange={() => toggleSelectImage(image._id)}
                    className="w-4 h-4 cursor-pointer rounded accent-blue-600" />
                </div>

                <div className="relative h-40 overflow-hidden">
                  {image.imageUrl ? (
                    <img src={resolveImageUrl(image.imageUrl)} alt={image.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${gradients[idx % gradients.length]} flex items-center justify-center`}>
                      <FiImage className="w-10 h-10 text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <button onClick={() => handleToggle(image._id)}
                      className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors backdrop-blur-sm"
                      title={image.isPublished ? "Unpublish" : "Publish"}>
                      {image.isPublished ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => setDeleteConfirm(image._id)}
                      className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white transition-colors" title="Delete">
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="absolute top-2 right-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${image.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {image.isPublished ? "Live" : "Draft"}
                    </span>
                  </div>
                </div>

                <div className="p-3">
                  <h3 className="font-semibold text-[#1e3a8a] text-sm truncate mb-1.5">{image.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${catColors[image.category] || catColors.Other}`}>
                      {image.category}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={() => handleToggle(image._id)}
                        className={`p-1 rounded-lg transition-colors ${image.isPublished ? "text-amber-400 hover:bg-amber-50" : "text-emerald-400 hover:bg-emerald-50"}`}>
                        {image.isPublished ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => setDeleteConfirm(image._id)} className="p-1 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}

            {images.length === 0 && (
              <div className="col-span-full text-center py-20">
                <FiImage className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No images in gallery</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Single Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Upload Image" size="md"
        footer={
          <>
            <button onClick={() => setModal(false)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Cancel</button>
            <button onClick={handleUpload} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a8a] disabled:opacity-70">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiUpload className="w-4 h-4" />}
              Upload
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {preview ? (
            <div className="relative w-full h-48 rounded-xl overflow-hidden border border-blue-100">
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              <button onClick={() => { setPreview(null); setForm((p) => ({ ...p, image: null })); }}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600">
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-blue-100 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
              <FiUpload className="w-8 h-8 text-blue-200 mb-2" />
              <span className="text-slate-400 text-sm">Click to upload image</span>
              <span className="text-slate-300 text-xs mt-1">JPG, PNG, WebP up to 5MB</span>
              <input type="file" onChange={handleFileChange} className="hidden" accept="image/*" />
            </label>
          )}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Title *</label>
            <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder="Image title"
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
            <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={2} placeholder="Optional description..."
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none resize-none text-slate-700 focus:border-blue-400" />
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Confirm Delete" size="sm"
        footer={
          <>
            <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Cancel</button>
            <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600">Delete</button>
          </>
        }
      >
        <p className="text-slate-600 text-sm">Are you sure you want to delete this image? This action cannot be undone.</p>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal isOpen={bulkModal} onClose={() => setBulkModal(false)} title="Upload Multiple Images" size="lg"
        footer={
          <>
            <button onClick={() => setBulkModal(false)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Cancel</button>
            <button onClick={handleBulkUpload} disabled={bulkSaving}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a8a] disabled:opacity-70">
              {bulkSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiUpload className="w-4 h-4" />}
              Upload {bulkForm.images.length > 0 ? `(${bulkForm.images.length})` : ""}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {bulkPreviews.length > 0 && (
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Selected Images ({bulkPreviews.length})</label>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {bulkPreviews.map((item, idx) => (
                  <div key={idx} className="relative h-24 rounded-xl overflow-hidden border border-blue-100">
                    <img src={item.preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      onClick={() => { setBulkForm((p) => ({ ...p, images: bulkForm.images.filter((_, i) => i !== idx) })); setBulkPreviews(bulkPreviews.filter((_, i) => i !== idx)); }}
                      className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600">
                      <FiX className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-blue-100 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
            <FiUpload className="w-8 h-8 text-blue-200 mb-2" />
            <span className="text-slate-400 text-sm">Click to select images</span>
            <span className="text-slate-300 text-xs mt-1">JPG, PNG, WebP up to 5MB (max 50 images)</span>
            <input type="file" multiple onChange={handleBulkFileChange} className="hidden" accept="image/*" />
          </label>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Title (applies to all images)</label>
            <input type="text" placeholder="e.g., Sports Day 2025, Annual Festival"
              value={bulkForm.title || ""} onChange={(e) => setBulkForm((p) => ({ ...p, title: e.target.value }))}
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-200" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
            <select value={bulkForm.category} onChange={(e) => setBulkForm((p) => ({ ...p, category: e.target.value }))}
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
            <p className="text-sm text-blue-600">Upload multiple photos at once. All images will use the same title. You can delete individual images anytime.</p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminGallery;
